import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useApp } from '../../lib/app-context';
import { mad } from '../../lib/format';
import { BarChart, HBars, LineChart, type Point, type Slice } from '../../components/charts';
import { Spinner } from '../../components/ui';
import { t } from '../../lib/i18n';

/**
 * The admin's view of how Bedly is doing: who is arriving, what is being
 * published and booked, and what it earns. Every number is computed on the
 * server from the same tables the rest of the app uses — nothing here is a
 * separate counter that could drift from the truth.
 */

type Analytics = {
  days: number;
  series: Record<
    | 'signups'
    | 'verified'
    | 'listingsPublished'
    | 'bookingRequests'
    | 'bookingsConfirmed'
    | 'feesCentimes'
    | 'messages',
    Point[]
  >;
  totals: {
    users: number;
    verifiedUsers: number;
    hosts: number;
    activeListings: number;
    pendingListings: number;
    confirmedBookings: number;
    feesCentimes: number;
  };
  window: {
    signups: number;
    verificationRate: number;
    bookingRequests: number;
    bookingsConfirmed: number;
    conversionRate: number;
    messages: number;
  };
  breakdowns: {
    signupMethod: Slice[];
    signupSource: Slice[];
    listingsByCity: Slice[];
    activeModes: Slice[];
    bookingModes: Slice[];
    topAffiliates: (Slice & { code: string })[];
  };
};

const MODE_NAMES: Record<string, string> = {
  COLOCATION: 'Colocation',
  MONTHLY: t("Au mois"),
  NIGHTLY: t("À la nuit"),
};

const TERRA = '#B04E33';
const TEAL = '#1F6F63';
const SAND = '#C9913D';

const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;

export default function AdminDashboard() {
  const { reportError } = useApp();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Analytics | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    api
      .get<Analytics>(`/admin/analytics?days=${days}`)
      .then((response) => active && setData(response))
      .catch(reportError);
    return () => {
      active = false;
    };
  }, [days, reportError]);

  return (
    <div className="stack stack--loose">
      <div className="row row--between row--wrap" style={{ gap: 10 }}>
        <h2 style={{ margin: 0 }}>{t("Tableau de bord")}</h2>
        <div className="row" style={{ gap: 6 }}>
          {[7, 30, 90, 365].map((option) => (
            <button
              key={option}
              type="button"
              className={`chip ${days === option ? 'chip--on' : ''}`}
              onClick={() => setDays(option)}
            >
              {option === 365 ? t("1 an") : t('{count} j', { count: option })}
            </button>
          ))}
        </div>
      </div>

      {data === null ? (
        <Spinner label={t("Calcul des statistiques…")} />
      ) : (
        <>
          <div className="kpis">
            {[
              [t("Membres"), data.totals.users, t('{count} vérifiés', { count: data.totals.verifiedUsers })],
              [t("Hôtes"), data.totals.hosts, t('{count} annonces en ligne', { count: data.totals.activeListings })],
              [t("À valider"), data.totals.pendingListings, t('annonces en attente')],
              [t("Réservations"), data.totals.confirmedBookings, t("confirmées au total")],
              [t("Frais encaissés"), mad(data.totals.feesCentimes), t('depuis le lancement')],
            ].map(([label, value, hint]) => (
              <div key={String(label)} className="card kpi">
                <span className="small muted">{label}</span>
                <span className="kpi__value">{value}</span>
                <span className="kpi__hint">{hint}</span>
              </div>
            ))}
          </div>

          <div className="kpis">
            {[
              [t("Inscriptions"), String(data.window.signups), t('sur {count} jours', { count: days })],
              [t("Emails vérifiés"), percent(data.window.verificationRate), t("des inscrits de la période")],
              [t("Demandes"), String(data.window.bookingRequests), t('{count} confirmées', { count: data.window.bookingsConfirmed })],
              [t("Conversion"), percent(data.window.conversionRate), t("demande → réservation")],
              [t("Messages"), String(data.window.messages), t('sur {count} jours', { count: days })],
            ].map(([label, value, hint]) => (
              <div key={label} className="card kpi">
                <span className="small muted">{label}</span>
                <span className="kpi__value">{value}</span>
                <span className="kpi__hint">{hint}</span>
              </div>
            ))}
          </div>

          <section className="card card--pad stack stack--tight">
            <h3 style={{ margin: 0 }}>{t("Croissance des membres")}</h3>
            <LineChart
              series={[
                { label: t("Inscriptions"), color: TERRA, points: data.series.signups },
                { label: t("Emails vérifiés"), color: TEAL, points: data.series.verified },
              ]}
            />
          </section>

          <section className="card card--pad stack stack--tight">
            <h3 style={{ margin: 0 }}>{t("Réservations")}</h3>
            <LineChart
              series={[
                { label: t("Demandes"), color: SAND, points: data.series.bookingRequests },
                { label: t("Confirmées"), color: TEAL, points: data.series.bookingsConfirmed },
              ]}
            />
          </section>

          <section className="card card--pad stack stack--tight">
            <h3 style={{ margin: 0 }}>{t("Frais Bedly encaissés")}</h3>
            <BarChart
              points={data.series.feesCentimes}
              color={TERRA}
              format={(value) => (value >= 100_000 ? `${Math.round(value / 100_000)}k` : mad(value))}
            />
          </section>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Annonces publiées")}</h3>
              <LineChart series={[{ label: t("Publiées"), color: TEAL, points: data.series.listingsPublished }]} />
            </section>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Messages échangés")}</h3>
              <LineChart series={[{ label: t("Messages"), color: SAND, points: data.series.messages }]} />
            </section>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Annonces en ligne par ville")}</h3>
              <HBars slices={data.breakdowns.listingsByCity} color={TERRA} />
            </section>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Types de location proposés")}</h3>
              <HBars slices={data.breakdowns.activeModes} color={TEAL} labels={MODE_NAMES} />
            </section>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Demandes par type")}</h3>
              <HBars slices={data.breakdowns.bookingModes} color={SAND} labels={MODE_NAMES} />
            </section>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Inscriptions : méthode")}</h3>
              <HBars slices={data.breakdowns.signupMethod} color={TEAL} />
            </section>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Inscriptions : provenance")}</h3>
              <HBars slices={data.breakdowns.signupSource} color={TERRA} />
            </section>
            <section className="card card--pad stack stack--tight">
              <h3 style={{ margin: 0 }}>{t("Meilleurs affiliés (")}{days} {t("j)")}</h3>
              <HBars
                slices={data.breakdowns.topAffiliates.map((row) => ({
                  label: `${row.label} · ${row.code}`,
                  value: row.value,
                }))}
                color={SAND}
              />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
